import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Filter, X } from "lucide-react";

/**
 * Reusable global dashboard filter bar.
 * Props:
 *   value: { company_id, sector_id, supervisor, month, year, view_mode }
 *   onChange: (newValue) => void
 *   companies: list of companies (for company filter)
 *   showCompany / showSector / showSupervisor / showMonth / showYear / showViewMode (bool, default true)
 */
const MONTH_LABELS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export default function GlobalDashboardFilters({
  value, onChange,
  companies = [],
  showCompany = true, showSector = true, showSupervisor = true,
  showMonth = true, showYear = true, showViewMode = true,
  showFormaContratacao = true, showTipoOperacional = true,
  testIdPrefix = "gdf",
}) {
  const [opts, setOpts] = useState({
    sectors: [], supervisors: [], years: [], view_modes: [],
    forma_contratacao: [], tipo_operacional: [],
  });

  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (value.company_id) params.company_id = value.company_id;
        const { data } = await api.get("/performance/filtros", { params });
        setOpts({
          sectors: data.sectors || [],
          supervisors: data.supervisors || [],
          years: data.years || [],
          view_modes: data.view_modes || [],
          forma_contratacao: data.forma_contratacao || [],
          tipo_operacional: data.tipo_operacional || [],
        });
      } catch (_) { /* silent */ }
    })();
  }, [value.company_id]);

  const update = (patch) => onChange({ ...value, ...patch });
  const clear = () => onChange({
    company_id: null, sector_id: null, supervisor: null,
    month: null, year: null, view_mode: "ultimos_12",
    forma_contratacao: null, tipo_operacional: null,
  });

  const months = [
    { v: null, label: "Todos os meses" },
    ...MONTH_LABELS.map((m, i) => ({ v: i + 1, label: m })),
  ];

  return (
    <div className="rounded-xl border border-border bg-white/95 backdrop-blur shadow-sm p-3 sm:p-4"
         data-testid={`${testIdPrefix}-bar`}>
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[#234935] font-semibold">
            <Filter className="h-3 w-3" /> Filtros globais
          </div>
          <Button size="sm" variant="ghost"
                  className="h-7 text-[11px] text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                  onClick={clear} data-testid={`${testIdPrefix}-clear`}>
            <X className="h-3 w-3 mr-1" /> Limpar
          </Button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          {showViewMode && (
            <div className="col-span-2 lg:col-span-2">
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Visão</label>
              <Select value={value.view_mode || "ultimos_12"}
                      onValueChange={(v) => update({ view_mode: v })}>
                <SelectTrigger className="h-9 text-[12.5px]" data-testid={`${testIdPrefix}-view-mode`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opts.view_modes.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showMonth && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Mês</label>
              <Select value={String(value.month || "all")}
                      onValueChange={(v) => update({ month: v === "all" ? null : parseInt(v) })}>
                <SelectTrigger className="h-9 text-[12.5px]" data-testid={`${testIdPrefix}-month`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {months.slice(1).map((m) => (
                    <SelectItem key={m.v} value={String(m.v)}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showYear && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Ano</label>
              <Select value={String(value.year || (opts.years[0] || new Date().getFullYear()))}
                      onValueChange={(v) => update({ year: parseInt(v) })}>
                <SelectTrigger className="h-9 text-[12.5px]" data-testid={`${testIdPrefix}-year`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opts.years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showCompany && companies.length > 0 && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Empresa</label>
              <Select value={value.company_id || "all"}
                      onValueChange={(v) => update({ company_id: v === "all" ? null : v })}>
                <SelectTrigger className="h-9 text-[12.5px]" data-testid={`${testIdPrefix}-company`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas (Grupo)</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showSector && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Setor</label>
              <Select value={value.sector_id || "all"}
                      onValueChange={(v) => update({ sector_id: v === "all" ? null : v })}>
                <SelectTrigger className="h-9 text-[12.5px]" data-testid={`${testIdPrefix}-sector`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {opts.sectors.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showSupervisor && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Supervisor</label>
              <Select value={value.supervisor || "all"}
                      onValueChange={(v) => update({ supervisor: v === "all" ? null : v })}>
                <SelectTrigger className="h-9 text-[12.5px]" data-testid={`${testIdPrefix}-supervisor`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {opts.supervisors.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showFormaContratacao && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Forma Contratação</label>
              <Select value={value.forma_contratacao || "all"}
                      onValueChange={(v) => update({ forma_contratacao: v === "all" ? null : v })}>
                <SelectTrigger className="h-9 text-[12.5px]" data-testid={`${testIdPrefix}-forma-contratacao`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {opts.forma_contratacao.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {showTipoOperacional && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Tipo Operacional</label>
              <Select value={value.tipo_operacional || "all"}
                      onValueChange={(v) => update({ tipo_operacional: v === "all" ? null : v })}>
                <SelectTrigger className="h-9 text-[12.5px]" data-testid={`${testIdPrefix}-tipo-operacional`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {opts.tipo_operacional.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const DEFAULT_FILTERS = {
  company_id: null,
  sector_id: null,
  supervisor: null,
  month: null,
  year: new Date().getFullYear(),
  view_mode: "ultimos_12",
  forma_contratacao: null,
  tipo_operacional: null,
};

export function filtersToParams(f) {
  const p = {};
  if (f.company_id) p.company_id = f.company_id;
  if (f.sector_id) p.sector_id = f.sector_id;
  if (f.supervisor) p.supervisor = f.supervisor;
  if (f.month) p.month = f.month;
  if (f.year) p.year = f.year;
  if (f.view_mode) p.view_mode = f.view_mode;
  if (f.forma_contratacao) p.forma_contratacao = f.forma_contratacao;
  if (f.tipo_operacional) p.tipo_operacional = f.tipo_operacional;
  return p;
}
